import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../../core/models/pagination.model';

export type TableStatus = 'available' | 'occupied' | 'reserved' | 'out_of_service';

export interface RestaurantTable {
  id: string;
  dining_area_id?: string | null;
  dining_area_name?: string | null;
  table_number: string;
  capacity: number;
  status: TableStatus;
  qr_code?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface RestaurantTableRequest {
  dining_area_id?: string | null;
  table_number: string;
  capacity?: number;
  status?: TableStatus;
  qr_code?: string | null;
  is_active?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class RestaurantTablesService {
  private readonly apiUrl = `${environment.apiUrl}/restaurant-tables`;

  constructor(private http: HttpClient) {}

  /** Full (unpaginated) list — used by dropdown/cross-feature consumers. */
  getAll(): Observable<ApiResponse<RestaurantTable[]>> {
    return this.http.get<ApiResponse<RestaurantTable[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /** Paginated list for the restaurant tables list page. */
  getPage(query: PaginationQuery = {}): Observable<Paginated<RestaurantTable>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<RestaurantTable[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  get(id: string): Observable<ApiResponse<RestaurantTable>> {
    return this.http.get<ApiResponse<RestaurantTable>>(`${this.apiUrl}/${id}`);
  }

  create(data: RestaurantTableRequest): Observable<ApiResponse<RestaurantTable>> {
    return this.http.post<ApiResponse<RestaurantTable>>(this.apiUrl, data);
  }

  update(id: string, data: Partial<RestaurantTableRequest>): Observable<ApiResponse<RestaurantTable>> {
    return this.http.put<ApiResponse<RestaurantTable>>(`${this.apiUrl}/${id}`, data);
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  toggleActive(id: string): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }
}
